<template>
  <v-overlay :color="'#fff'" :value="loading" z-index="10" opacity="1">
    <div class="overlay-content">
      <span class="loader-text">
        {{ $t("billing.processing_payment") }}
      </span>
      <v-progress-linear
        :value="progress"
        color="primary"
        background-color="element-bg-300"
        height="6"
        rounded></v-progress-linear>
    </div>
  </v-overlay>
</template>

<script>
  import definedAbility from "@/ability";
  import { mixpanelWrapper } from "@/@core/libs/mixpanel";
  import store from "@/store";
  export default {
    data() {
      return {
        ability: definedAbility(),
        response: this.$route.query,
        loading: false,
        progress: 0,
        stripe: null,
        intent: null,
        intentType: null,
        billingInformation: {},
        selectedPlan: {},
      };
    },
    beforeMount() {
      this.displayLoader();
      this.handlePayment();
      this.billingInformation = JSON.parse(this.response.billingInformation);
      this.selectedPlan = JSON.parse(this.response.selectedPlan);
    },
    methods: {
      async handlePayment() {
        this.stripe = await this.$store.dispatch("billing/getStripe");
        this.intentType = this.response?.setup_intent ? "setup" : "payment";
        const retrieveIntent =
          this.intentType === "setup" ? this.stripe.retrieveSetupIntent : this.stripe.retrievePaymentIntent;

        const clientSecret =
          this.intentType === "setup"
            ? this.response.setup_intent_client_secret
            : this.response.payment_intent_client_secret;

        const response = await retrieveIntent(clientSecret);
        this.intent = this.intentType === "setup" ? response.setupIntent : response.paymentIntent;

        switch (this.intent.status) {
          case "succeeded":
            await this.completePayment();
            break;
          case "requires_payment_method":
            this.$admin.toast.error(this.$t("billing.payment_failed"));
            this.$router.push({ name: "billing" });
            break;
          default:
            this.$admin.toast.error(this.$t("generic.error"));
            this.$router.push({ name: "billing" });
            break;
        }
      },
      async completePayment() {
        try {
          const purchaseConfirmResponse = await this.confirmPurchase(this.intent.id, this.intentType);
          const subscriptionId = purchaseConfirmResponse.data.id;

          const { id, email, phoneNumber: phone } = this.$store.state.user;

          // Set "Owner" Role when payment is successful and as soon as organization is created successfully
          this.drip.push([
            "identify",
            {
              user_id: id,
              email,
              step: 2,
              phone,
              address1: this.billingInformation.line1,
              city: this.billingInformation.city || "",
              state: this.billingInformation.state,
              zip: this.billingInformation.postalCode,
              country: this.billingInformation.country,
              Role: "Owner",
            },
          ]);

          // Push purchase event to GTM
          this.gtm.push({
            event: "purchase",
            plan_value: this.selectedPlan.price,
          });

          // Conversion for Google Ads free trial start
          window.gtag &&
            window.gtag("event", "conversion", {
              send_to: "AW-16455619013/AHIsCNK846AZEMWj06Y9",
              value: this.selectedPlan.price,
              currency: "USD",
              transaction_id: subscriptionId,
            });

          // Reset discounts on successful payment
          this.$store.commit("SET_PAYMENT_DISCOUNTS", []);

          // Refresh user data - so that the organization is set and we can access the dashboard.
          await store.dispatch("refreshAndStoreAllUserData");

          mixpanelWrapper.track("Onboarding Started", { previousPage: store.getters["getPreviousRoute"] });

          if (process.env.VUE_APP_DRIP_ENABLED === "true" && this.ability.can("view", "drip survey modal")) {
            this.$store.commit("SET_DISPLAY_DRIP_SURVEY_MODAL", true);
          }

          // Redirect to the dashboard
          this.$router.push({ name: "dashboard" });
        } catch (e) {
          this.$admin.toast.error(e.message || this.$t("generic.error"));
          this.$router.push({ name: "billing" });
        }
      },
      /**
       * This is where the organization is actually created in BE;
       * For now, organization name is by default "<First Name>'s Organization"
       */
      async confirmPurchase(intentId, intentType) {
        // GA Client ID is used for tracking in Backend.
        const gaClientId = window.gaGlobal?.vid || "";
        const response = await this.$admin.dataProvider.customHttpCall("POST", "subscriptions/confirm", {
          intentId,
          intentType,
          gaClientId,
        });

        return response;
      },
      displayLoader() {
        this.loading = true;
        /**
         * Simulated, non-linear loading. Loads as long as possible, slowing down the further it gets.
         */
        this.interval = setInterval(() => {
          const increment = (100 - this.progress) / 10;
          if (this.progress < 95) {
            this.progress += increment;
          } else {
            this.progress += 0.05;
          }
          if (this.progress > 99.5) {
            this.progress = 99.5;
          }
        }, 250);
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";
  @import "@core/preset/preset/typography/index.scss";

  .overlay-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 320px;

    .loader-text {
      color: get-color("subtitle");
      @include text-body-2();
      margin-bottom: 16px;
    }
  }
</style>
